/**
 * Original chiptune race music written for this remake (not a transcription of the Game Boy score).
 * Instrumentation echoes the original's sound-chip layout: pulse lead, wave-style bass, noise drums.
 */
export interface Track {
  name: string;
  bpm: number;
  roots: number[]; // chord roots (MIDI) per bar, 16 sixteenth-steps per bar
  minor: boolean[]; // chord quality per bar
  lead: number[][]; // per bar: 16 chord-tone indices (-1 = rest) into CHORD_TONES
  bassOct: number[]; // 16-step bass octave offsets (-1 rest)
  drums: string; // 16 chars: k kick, s snare, h hat, . rest
}
// indices: 0 root, 1 third, 2 fifth, 3 octave, 4 octave+third, 5 octave+fifth
const R = -1;
export const TRACKS: Track[] = [
  {
    name: "Pole Position",
    bpm: 144,
    roots: [57, 53, 60, 55],
    minor: [true, false, false, false],
    lead: [
      [0, R, 1, R, 2, R, 1, R, 0, R, 1, 2, 3, R, 2, R],
      [0, R, 1, R, 2, R, 1, R, 0, R, 1, 2, 3, R, 2, R],
      [2, R, 3, R, 4, R, 3, R, 2, R, 3, 4, 5, R, 4, R],
      [1, R, 2, R, 3, R, 2, R, 1, R, 2, 3, 5, R, 3, 2],
    ],
    bassOct: [0, 0, 12, 0, 0, 0, 12, 0, 0, 0, 12, 0, 0, 12, 0, R],
    drums: "k.h.s.h.k.h.s.hh",
  },
  {
    name: "Night Straight",
    bpm: 132,
    roots: [62, 58, 53, 60],
    minor: [true, false, false, false],
    lead: [
      [3, R, R, 2, R, R, 1, R, 2, R, R, 1, R, 0, R, R],
      [3, R, R, 2, R, R, 1, R, 2, R, R, 1, R, 0, R, R],
      [3, R, R, 4, R, R, 3, R, 2, R, R, 3, R, 1, R, R],
      [5, R, R, 4, R, R, 3, R, 2, R, 3, R, 4, R, 3, R],
    ],
    bassOct: [0, R, 0, R, 12, R, 0, R, 0, R, 0, R, 12, R, 0, 0],
    drums: "k.h.sh.hk.hhs.h.",
  },
  {
    name: "Last Lap",
    bpm: 156,
    roots: [64, 60, 55, 62],
    minor: [true, false, false, false],
    lead: [
      [0, 1, 2, 3, 2, 1, 2, 3, 4, 3, 2, 3, 2, 1, 0, 1],
      [0, 1, 2, 3, 2, 1, 2, 3, 4, 3, 2, 3, 2, 1, 0, 1],
      [2, 3, 4, 5, 4, 3, 4, 5, 4, 3, 2, 3, 4, 3, 2, 1],
      [1, 2, 3, 4, 3, 2, 3, 4, 5, 4, 3, 4, 3, 2, 1, 2],
    ],
    bassOct: [0, 0, 0, 0, 12, 0, 0, 0, 0, 0, 12, 0, 0, 0, 12, 0],
    drums: "k.hhs.hhk.hhs.sh",
  },
];

/** Title / menu theme: slower and more stately than the race tunes. 8 bars. */
export const TITLE: Track = {
  name: "Grid Walk",
  bpm: 112,
  roots: [48, 44, 51, 46, 48, 44, 46, 43],
  minor: [true, false, false, false, true, false, false, false],
  lead: [
    [3, R, R, R, 2, R, 3, R, 4, R, R, R, 3, R, 2, R],
    [3, R, R, R, 2, R, 3, R, 5, R, R, R, 4, R, 3, R],
    [2, R, R, R, 3, R, 4, R, 5, R, R, R, 4, R, 3, R],
    [1, R, 2, R, 3, R, 2, R, 1, R, R, R, 0, R, R, R],
    [3, R, R, R, 2, R, 3, R, 4, R, R, R, 3, R, 2, R],
    [3, R, R, R, 2, R, 3, R, 5, R, R, R, 4, R, 3, R],
    [4, R, 5, R, 4, R, 3, R, 2, R, 3, R, 4, R, 5, R],
    [5, R, R, R, R, R, R, R, 3, R, R, R, 2, R, R, R],
  ],
  bassOct: [0, R, R, R, 0, R, 12, R, 0, R, R, R, 0, R, 12, R],
  drums: "k...s...k.k.s..h",
};

const mtof = (m: number) => 440 * Math.pow(2, (m - 69) / 12);
export function chordTone(root: number, minor: boolean, idx: number): number {
  const t = [0, minor ? 3 : 4, 7, 12, 12 + (minor ? 3 : 4), 19];
  return root + 24 + t[idx]; // lead sits two octaves above the bass root
}
export { mtof };

/**
 * Grid fanfare, played once while the starting lights count down (3.67 s = two bars at 131 BPM, so it ends as the lights go out).
 * Original: a rising arpeggio in A minor that builds on the dominant with a snare roll, then the race tune takes over on the tonic.
 */
export const INTRO: Track = {
  name: "Lights Out",
  bpm: 131,
  roots: [57, 52],
  minor: [true, false],
  lead: [
    [0, R, 1, R, 2, R, 3, R, 2, R, 3, R, 4, R, 5, R],
    [3, R, 4, R, 3, R, 4, R, 5, R, 5, R, 5, 5, 5, 5],
  ],
  bassOct: [0, R, 12, R, 0, R, 12, R, 0, R, 12, R, 0, R, 12, R],
  drums: "k.h.k.h.k.hhssss",
};

/** Original victory tune for the podium: a bright major-key march (C, F, G, C) that plays once, about 7 s. */
export const VICTORY: Track = {
  name: "Champions",
  bpm: 140,
  roots: [60, 65, 67, 60],
  minor: [false, false, false, false],
  lead: [
    [2, R, 2, 3, 4, R, 4, R, 3, R, 4, R, 5, R, R, R],
    [3, R, 3, 4, 5, R, 5, R, 4, R, 5, R, 5, R, R, R],
    [4, R, 4, 5, 5, R, 5, R, 5, R, 4, R, 3, R, 4, R],
    [5, R, 5, 5, 5, R, 5, 4, 3, 3, 4, 4, 5, 5, 5, 5],
  ],
  bassOct: [0, R, 7, R, 0, R, 7, R, 0, R, 7, R, 0, R, 7, R],
  drums: "k.h.s.h.k.h.s.hh",
};
